import React, { useState } from 'react';
import { useToast } from '../../context/ToastContext';

const DISCOUNT_TIERS = {
  AVD: ['AVD_BELOW_20', 'AVD_BELOW_30', 'AVD_ABOVE_30'],
  OTC: ['OTC_BELOW_5', 'OTC_BELOW_10', 'OTC_BELOW_15', 'OTC_BELOW_20', 'OTC_BELOW_25', 'OTC_BELOW_30', 'OTC_ABOVE_30'],
  COS: ['COS_BELOW_20', 'COS_BELOW_30', 'COS_BELOW_40', 'COS_ABOVE_40'],
  EQP: ['EQP_BELOW_20', 'EQP_BELOW_30', 'EQP_BELOW_40', 'EQP_ABOVE_40'],
  SGL: ['SGL_BELOW_20', 'SGL_BELOW_30', 'SGL_BELOW_40', 'SGL_ABOVE_40'],
  INJ: ['INJ_BELOW_15', 'INJ_BELOW_20', 'INJ_BELOW_25', 'INJ_BELOW_30', 'INJ_ABOVE_30'],
  MED: ['MED_BELOW_10', 'MED_BELOW_20', 'MED_BELOW_30', 'MED_BELOW_40', 'MED_BELOW_50', 'MED_ABOVE_50'],
  GEN: ['GEN_BELOW_10', 'GEN_BELOW_20', 'GEN_BELOW_30', 'GEN_BELOW_40', 'GEN_BELOW_50', 'GEN_ABOVE_50'],
  DISCOUNT: ['DISCOUNT_1', 'DISCOUNT_2', 'DISCOUNT_3', 'DISCOUNT_4']
};

function ManualMedicineEntry({ onSave, onClose }) {
  const toast = useToast();
  const [medicine, setMedicine] = useState({
    medicine_name: '',
    manufacturer_name: '',
    content: '',
    size: 1,
    batch: '',
    expiry: '',
    mrp: '',
    quantity: '',
    packing_size: '',
    discount_tier: ''
  });

  const [selectedCategory, setSelectedCategory] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!medicine.medicine_name || !medicine.mrp || !medicine.quantity || !medicine.discount_tier) {
      toast.error('Please fill all required fields, including discount tier.');
      return;
    }

    // Create a formatted medicine object
    const formattedMedicine = {
      medicine_name: medicine.medicine_name,
      manufacturer_name: medicine.manufacturer_name || '',
      content: medicine.content || '',
      size: medicine.size || 1,
      mrp: parseFloat(medicine.mrp),
      quantity: parseInt(medicine.quantity),
      batch: medicine.batch || 'Not Specified',
      expiry: medicine.expiry || '',
      expiry_label: medicine.expiry 
        ? new Date(medicine.expiry).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        : '-',
      packing_size: medicine.packing_size || '',
      discount_tier: medicine.discount_tier
    };

    onSave(formattedMedicine);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">Add Custom Medicine</h3>
          <button 
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <i className="material-icons-outlined">close</i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-4">
          <div className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Category*
                </label>
                <select
                  className="input-field"
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setMedicine({ ...medicine, discount_tier: '' });
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {Object.keys(DISCOUNT_TIERS).map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Tier*
                  </label>
                  <select
                    className="input-field"
                    value={medicine.discount_tier}
                    onChange={(e) => setMedicine({ ...medicine, discount_tier: e.target.value })}
                    required
                  >
                    <option value="">Select Tier</option>
                    {DISCOUNT_TIERS[selectedCategory].map(tier => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medicine Name*
              </label>
              <input
                required
                type="text"
                className="input-field"
                value={medicine.medicine_name}
                onChange={(e) => setMedicine({ ...medicine, medicine_name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manufacturer
              </label>
              <input
                type="text"
                className="input-field"
                value={medicine.manufacturer_name}
                onChange={(e) => setMedicine({ ...medicine, manufacturer_name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salt <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                className="input-field"
                value={medicine.content}
                onChange={(e) => setMedicine({ ...medicine, content: e.target.value })}
                placeholder="E.g., Paracetamol (500mg)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                className="input-field"
                value={medicine.size}
                onChange={(e) => setMedicine({ ...medicine, size: e.target.value })}
                placeholder="E.g., Paracetamol (500mg)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Batch Number <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={medicine.batch}
                  onChange={(e) => setMedicine({ ...medicine, batch: e.target.value })}
                  placeholder="Enter batch number if known"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Date <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={medicine.expiry}
                  onChange={(e) => setMedicine({ ...medicine, expiry: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MRP*
                </label>
                <input
                  required
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={medicine.mrp}
                  onChange={(e) => setMedicine({ ...medicine, mrp: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity*
                </label>
                <input
                  required
                  type="number"
                  className="input-field"
                  value={medicine.quantity}
                  onChange={(e) => setMedicine({ ...medicine, quantity: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Packing Size <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                className="input-field"
                value={medicine.packing_size}
                placeholder="e.g., 1 Strip of 10 Tablets"
                onChange={(e) => setMedicine({ ...medicine, packing_size: e.target.value })}
              />
            </div>
          </div>
        </form>

        <div className="border-t p-4 mt-auto">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btn-primary"
            >
              Add Medicine
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManualMedicineEntry; 